// Einträge des Avatar-Menüs je Plattform (UX §2.5, §2.7, §2.10, §2.11): Konto, Plattform-Wechsel
// (Admin nur mit Recht), Hilfe – plus die Beiträge der Module (Registry `accountMenuItems`).
import { type AccountMenuEntry, Monogram } from '@bar/ui/basis';
import {
  ArrowSquareOut,
  BellSimple,
  ChatsCircle,
  Keyboard,
  Question,
  UserGear,
} from '@phosphor-icons/react';
import type { AccountMenuItem, PlatformKey } from '@web/registry/types';

export interface EntryContext {
  platform: PlatformKey;
  adminCapable: boolean;
  openShortcuts: () => void;
  openHelp: () => void;
}

const platformLeading = (platform: PlatformKey) => (
  <Monogram size={24} platform={platform === 'people' ? undefined : platform} />
);

/** Plattform-Wechsel öffnet eine andere App (UX §2.11, DESIGN §3.6.1). */
const external = <ArrowSquareOut size={14} aria-hidden className="shrink-0 text-fg-secondary" />;

export function buildAccountEntries(
  ctx: EntryContext,
  contributed: readonly AccountMenuItem[],
): AccountMenuEntry[] {
  const { platform, adminCapable } = ctx;
  const local = platform === 'people';
  const entries: AccountMenuEntry[] = [
    {
      id: 'account-settings',
      label: 'Account settings',
      group: 'account',
      icon: UserGear,
      href: '/account',
      reload: !local,
    },
  ];
  if (platform === 'ai') {
    entries.push({
      id: 'notifications',
      label: 'Notifications',
      group: 'account',
      icon: BellSimple,
      href: '/notifications',
      reload: true,
    });
  }
  for (const item of contributed) {
    entries.push({
      id: `registry:${item.key}`,
      label: item.label,
      group: item.group === 'settings' ? 'account' : item.group,
      icon: item.icon,
      href: item.href,
      onSelect: item.onSelect,
      trailing: item.trailing,
    });
  }
  if (platform !== 'people') {
    entries.push({
      id: 'to-people',
      label: 'Switch to People app',
      group: 'platforms',
      leading: platformLeading('people'),
      trailing: external,
      href: '/',
      reload: true,
    });
  }
  if (platform !== 'ai') {
    entries.push({
      id: 'to-ai',
      label: 'Bar AI',
      group: 'platforms',
      leading: platformLeading('ai'),
      trailing: external,
      href: '/ai/',
      reload: true,
    });
  }
  if (platform !== 'admin' && adminCapable) {
    entries.push({
      id: 'to-admin',
      label: 'Switch to Bar Admin',
      group: 'platforms',
      leading: platformLeading('admin'),
      trailing: external,
      href: '/admin/',
      reload: true,
    });
  }
  if (platform === 'admin') {
    entries.push({
      id: 'open-chat',
      label: 'Open chat',
      group: 'platforms',
      icon: ChatsCircle,
      href: '/chat',
      reload: true,
    });
  }
  entries.push(
    {
      id: 'shortcuts',
      label: 'Keyboard shortcuts',
      group: 'help',
      icon: Keyboard,
      onSelect: ctx.openShortcuts,
    },
    { id: 'help', label: 'Help', group: 'help', icon: Question, onSelect: ctx.openHelp },
  );
  return entries;
}
